import { openPaywallWithLayout } from './commerceController';
import type { PrototypeState } from './controllerState';
import { closeModal, currentAiCreateViewGeneration, openModal } from './navigationController';
import { notifyError } from './prototypeRuntimeSupport';
import type { Root } from './prototypeTypes';
import { AI_CHAPTER_AUTO_PLAN_PRESET, aiChapterDefaultPreset, aiChapterPresetBounds, isAiChapterPresetSelectable } from '../../domain/aiChapterTargetPolicy';
import { reconcileVisibleWizardTurnstile } from './modalTurnstileLifecycle';
import { ensureBusinessOverlayMounted } from './businessOverlayLifecycle';

const AI_CHAPTER_LOW_WARNING_PRESET = '1-5';
const launchGenerations = new WeakMap<Root, number>();

export function invalidateAiCreateLaunch(root: Root): void {
	launchGenerations.set(root, (launchGenerations.get(root) ?? 0) + 1);
}

/** 反映当前选中的 preset 到隐藏载体 #aiTopicChapters（提交仍走现有字段）。 */
function applyAiChapterPresetSelection(root: Root, value: string): void {
	const input = root.querySelector<HTMLInputElement>('#aiTopicChapters');
	if (input) input.value = value;
	updateAiChapterCreditNote(root, value);
}

/** 将当前区间和已读取的余额折算成用户可见的最大预留额度。 */
function updateAiChapterCreditNote(root: Root, preset: string): void {
	const copy = root.querySelector<HTMLElement>('[data-ai-chapter-credit-copy]');
	if (!copy) return;
	const available = Number(root.querySelector<HTMLElement>('#aiCreatedList')?.dataset.quotaAvailable);
	const bounds = aiChapterPresetBounds(preset);
	if (!Number.isSafeInteger(available) || available <= 0 || !bounds) return;
	const held = Math.min(bounds.hi, available);
	copy.textContent = `We’ll temporarily hold up to ${held} AI Credits for this run. When your topic is ready, we’ll charge only for the chapters created and return any unused credits automatically.`;
}

/** 仍然选择：确认 1-5，落 carrier 并关闭确认框。 */
export function confirmAiChapterLowRange(root: Root): void {
	applyAiChapterPresetSelection(root, AI_CHAPTER_LOW_WARNING_PRESET);
	closeModal(root, 'aiChapterLowRangeModal');
}

/** 取消：还原到之前选中的预设，避免 radio 选中态与 carrier 不一致。 */
export function closeAiChapterLowRange(root: Root): void {
	const dialog = root.querySelector<HTMLElement>('#aiChapterLowRangeModal');
	const previous = dialog?.dataset.previousSelection ?? '';
	if (previous) {
		const radios = Array.from(root.querySelectorAll<HTMLInputElement>('input[name="aiChapterPreset"]'));
		const target = radios.find((radio) => radio.value === previous && !radio.disabled);
		if (target) {
			for (const radio of radios) radio.checked = radio === target;
			applyAiChapterPresetSelection(root, target.value);
		}
	}
	closeModal(root, 'aiChapterLowRangeModal');
}

/** 一次性绑定 preset radio 的 change 处理（dataset 防重）。 */
function wireAiChapterPresets(root: Root): void {
	const radios = Array.from(root.querySelectorAll<HTMLInputElement>('input[name="aiChapterPreset"]'));
	for (const radio of radios) {
		if (radio.dataset.aiPresetBound === 'true') continue;
		radio.dataset.aiPresetBound = 'true';
		radio.addEventListener('change', () => {
			if (!radio.checked) return;
			// 1-5 仅在用户显式点击（且当前已提交值不是 1-5）时弹确认框；
			// 程序化默认选中 1-5（余额 1-5 时唯一可选项）不弹。
			if (radio.value === AI_CHAPTER_LOW_WARNING_PRESET) {
				const previous = (root.querySelector<HTMLInputElement>('#aiTopicChapters')?.value ?? '').trim();
				if (previous !== AI_CHAPTER_LOW_WARNING_PRESET) {
					const dialog = root.querySelector<HTMLElement>('#aiChapterLowRangeModal');
					if (dialog) dialog.dataset.previousSelection = previous;
					openModal(root, 'aiChapterLowRangeModal');
					return; // 等用户在确认框里决定，先不落 carrier
				}
			}
			applyAiChapterPresetSelection(root, radio.value);
		});
	}
}

/** 选中指定预设（radio + carrier 同步），不触发 change。 */
function selectPreset(root: Root, value: string): void {
	const radios = Array.from(root.querySelectorAll<HTMLInputElement>('input[name="aiChapterPreset"]'));
	const target = radios.find((radio) => radio.value === value);
	if (!target) return;
	for (const radio of radios) radio.checked = radio === target;
	applyAiChapterPresetSelection(root, value);
}

/** 按可用额度 gating 预设（lo ≤ 余额）；新表单与失效草稿回退到 Auto-Plan。 */
function syncAiChapterPresets(root: Root, available: number): void {
	const radios = Array.from(root.querySelectorAll<HTMLInputElement>('input[name="aiChapterPreset"]'));
	if (!radios.length) return;
	for (const radio of radios) {
		radio.disabled = !isAiChapterPresetSelectable(radio.value, available);
	}
	const carrier = root.querySelector<HTMLInputElement>('#aiTopicChapters')?.value.trim() ?? '';
	const preserved = radios.find((radio) => radio.checked && radio.value === carrier && !radio.disabled);
	if (preserved) {
		updateAiChapterCreditNote(root, preserved.value);
		return;
	}
	const preferred = radios.some((radio) => radio.value === AI_CHAPTER_AUTO_PLAN_PRESET && !radio.disabled)
		? AI_CHAPTER_AUTO_PLAN_PRESET
		: aiChapterDefaultPreset(available);
	if (preferred) selectPreset(root, preferred);
}

/** 重置 preset 到 UI 默认 Auto-Plan，供 resetAiCreateWizardForm 复用。 */
export function resetAiCreateChapterPresets(root: Root): void {
	const radios = Array.from(root.querySelectorAll<HTMLInputElement>('input[name="aiChapterPreset"]'));
	if (!radios.length) return;
	const selected = radios.find((radio) => !radio.disabled && radio.value === AI_CHAPTER_AUTO_PLAN_PRESET)
		?? radios.find((radio) => !radio.disabled && radio.value === '9-12')
		?? radios.find((radio) => !radio.disabled && radio.value === '6-8')
		?? radios.find((radio) => !radio.disabled && radio.value === '1-5');
	selectPreset(root, selected?.value ?? '');
}

function openAiCreatePaywall(root: Root, state: PrototypeState): void {
	const note = root.querySelector<HTMLElement>('#paywallIntentNote');
	if (note) {
		note.textContent = state.isLoggedIn
			? 'You need available AI Create credits. Earn referral credits or upgrade for a monthly allowance.'
			: 'Explore AI Create first, then choose a plan or sign in when you are ready to generate a private course.';
		note.style.display = '';
	}
	const target = root.querySelector<HTMLElement>('#paywallTargetPlan');
	if (target) target.textContent = 'Pro';
	openPaywallWithLayout(root, state, { setIntent: () => undefined });
}

async function readAiQuotaAvailable(state: PrototypeState): Promise<number | null> {
	try {
		const response = await state.runtime.api.getQuotaUsage(1);
		const rawAvailable = Number(response.summary.remaining);
		if (!Number.isFinite(rawAvailable)) return null;
		const available = Math.max(0, rawAvailable);
		return available;
	} catch {
		return null;
	}
}

export async function launchAiCreate(root: Root, state: PrototypeState, returnFocus?: HTMLElement | null): Promise<void> {
	ensureBusinessOverlayMounted(root, 'aiCreateModal');
	ensureBusinessOverlayMounted(root, 'aiChapterLowRangeModal');
	const generation = (launchGenerations.get(root) ?? 0) + 1;
	launchGenerations.set(root, generation);
	const viewGeneration = currentAiCreateViewGeneration(root);
	if (!state.isLoggedIn) {
		openAiCreatePaywall(root, state);
		return;
	}
	const available = await readAiQuotaAvailable(state);
	if (!ownsAiCreateLaunch(root, generation, viewGeneration, returnFocus)) return;
	const list = root.querySelector<HTMLElement>('#aiCreatedList');
	if (list && available !== null) list.dataset.quotaAvailable = String(available);
	if (available == null) {
		notifyError('Could not verify your AI Create credits. Please try again.');
		return;
	}
	if (available <= 0) {
		if (state.effectiveAccessPlan === 'max') notifyError('You have used all available AI Create credits. New credits arrive at your next reset.');
		else openAiCreatePaywall(root, state);
		return;
	}
	wireAiChapterPresets(root);
	syncAiChapterPresets(root, available);
	openModal(root, 'aiCreateModal', returnFocus);
	// Re-authentication closes every modal but intentionally preserves the
	// wizard fields. If the preserved form was already on Step 3, reopening
	// must also mount a fresh one-shot Turnstile challenge; otherwise the
	// submit control remains permanently locked after a revoked session.
	await reconcileVisibleWizardTurnstile(root, state.runtime);
}

function ownsAiCreateLaunch(root: Root, generation: number, viewGeneration: number, trigger?: HTMLElement | null): boolean {
	if (launchGenerations.get(root) !== generation) return false;
	if (currentAiCreateViewGeneration(root) !== viewGeneration) return false;
	if (!trigger) return true;
	if (!trigger.isConnected) return false;
	const view = trigger.closest<HTMLElement>('.view');
	return !view || view.classList.contains('active');
}
